import { env } from '@core/config/env';
import { getGameId, getStoredToken } from '@core/utils/auth';
import { handleApiResponse } from '@core/index';
import type { ApiResponse } from '@shared/interface';
import type { DynamicFieldConfig, DropdownGroup, PriceMapping } from '../types';

const API_BASE_URL = env.apiBaseUrl;

const getAuthHeaders = () => {
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'ngrok-skip-browser-warning': '69420',
    ...(token && { Authorization: `Bearer ${token}` }),
    'Content-Type': 'application/json',
  };
  return headers;
};

// ============================================
// DROPDOWN FIELDS API (for managing dropdown options)
// ============================================

/**
 * Fetch all dropdown fields for a specific module type
 * GET /playbook/dropdown/{moduleType}
 */
export const listDropdownFields = async (moduleType: string): Promise<ApiResponse<DynamicFieldConfig[]>> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/playbook/dropdown/${moduleType.toLowerCase()}/${gameId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<ApiResponse<DynamicFieldConfig[]>>(response, 'Failed to fetch dropdown fields');
};

/**
 * Update dropdown fields (bulk update)
 * PUT /playbook
 * Payload: { fields: DynamicFieldConfig[] }
 */
export const updateDropdownFields = async (
  fields: DynamicFieldConfig[],
): Promise<ApiResponse<DynamicFieldConfig[]>> => {
  const response = await fetch(`${API_BASE_URL}/playbook`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ fields }),
  });
  return handleApiResponse<ApiResponse<DynamicFieldConfig[]>>(response, 'Failed to update dropdown fields');
};

export const listPlaybookFields = async (moduleType: string): Promise<ApiResponse<DynamicFieldConfig[]>> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/playbook/${moduleType.toLowerCase()}/${gameId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<ApiResponse<DynamicFieldConfig[]>>(response, 'Failed to fetch dynamic fields');
};

export const createPlaybookField = async (
  moduleType: string,
  payload: Omit<DynamicFieldConfig, 'id'>,
): Promise<ApiResponse<DynamicFieldConfig>> => {
  const reqPayload = {
    module_type: moduleType,
    fields: [payload],
  };
  const response = await fetch(`${API_BASE_URL}/playbook`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(reqPayload),
  });
  return handleApiResponse<ApiResponse<DynamicFieldConfig>>(response, 'Failed to create dynamic field');
};

export const updatePlaybookField = async (payload: DynamicFieldConfig): Promise<ApiResponse<DynamicFieldConfig>> => {
  const reqPayload = {
    fields: [{ ...payload }],
  };
  const response = await fetch(`${API_BASE_URL}/playbook`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(reqPayload),
  });
  return handleApiResponse<ApiResponse<DynamicFieldConfig>>(response, 'Failed to update dynamic field');
};

export const deletePlaybookField = async (id: string): Promise<{ success: boolean }> => {
  const response = await fetch(`${API_BASE_URL}/playbook/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<{ success: boolean }>(response, 'Failed to delete dynamic field');
};

// ============================================
// PRICE MAPPING API
// ============================================

/**
 * Fetch all price mappings for a game
 * GET /playbook/pricemapping/:gameId
 */
export const listPriceMappings = async (): Promise<ApiResponse<PriceMapping[]>> => {
  const gameId = getGameId();
  const response = await fetch(`${API_BASE_URL}/playbook/pricemapping/${gameId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleApiResponse<ApiResponse<PriceMapping[]>>(response, 'Failed to fetch price mappings');
};

/**
 * Create a new price mapping
 * POST /playbook/price
 */
export const createPriceMapping = async (payload: {
  gameId: number;
  price: number;
  platform: number;
  value: string;
}): Promise<ApiResponse<PriceMapping>> => {
  const response = await fetch(`${API_BASE_URL}/playbook/price`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse<ApiResponse<PriceMapping>>(response, 'Failed to create price mapping');
};

/**
 * Update an existing price mapping
 * PUT /playbook/price/:offer_price_mapping_id
 */
export const updatePriceMapping = async (
  id: number,
  payload: {
    price: number;
    platform: number;
    value: string;
  },
): Promise<ApiResponse<PriceMapping>> => {
  const response = await fetch(`${API_BASE_URL}/playbook/price/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse<ApiResponse<PriceMapping>>(response, 'Failed to update price mapping');
};
